/**
 * Form15_TelemedicineConsent.jsx
 * Collection: telemedicine_consents
 * Redirect: /forms/16-wellness-preventive-assessment
 *
 * Source: Telemedicine_Consent.pdf — 7 numbered consent points,
 * patientName / patientAge / practiceName props,
 * responsibleParty + relationship + signature.
 */
import React, { useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { FormShell, Field, SigBlock, saveToMongo, downloadPDF, useToast, C } from "../shared/formKit";

const API_URL = "/api/forms/telemedicine-consent";
const NEXT    = "/forms/16-wellness-preventive-assessment";

const CONSENT_POINTS = [
  `I understand that my health care provider wishes me to engage in a telemedicine consultation and/or follow-up services (on some days and in situations) where I cannot get into the office to see my provider.`,
  `My health care provider has explained to me how the video conferencing technology will be used to provide the services — it will not be the same as a direct patient/health care provider visit due to the fact that I will not be in the same room as my health care provider.`,
  `I understand there are potential risks to the technology, including interruptions, unauthorized access, and technical difficulties. I understand that my health care provider or I can discontinue the telemedicine consult/visit if the videoconferencing connection is not adequate for the situation.`,
  `I understand that my healthcare information may be shared with other individuals for scheduling and billing purposes. In some cases, others may also be present during the consultation to operate the video equipment. All such persons will maintain confidentiality. I further understand that I have the right to: (1) omit specific details of my medical history/physical examination that are personally sensitive to me; (2) ask non-medical personnel to leave the telemedicine examination room; and/or (3) terminate the consultation at any time.`,
  `I have had the alternatives to a telemedicine consultation explained to me, and I am choosing to participate in a telemedicine consultation/service. I understand that some parts of the exam involving physical tests may be conducted by individuals at my location at the direction of the consulting health care provider.`,
  `In some emergent consultation or circumstances, I understand that the responsibility of the telemedicine consulting specialist is to advise (make a recommendation to) my local practitioner or provider, and that the specialist's responsibility will conclude upon the termination of the video conference connection.`,
  `I have had a direct conversation with my doctor, during which I had the opportunity to ask questions regarding this procedure. My questions have been answered and the risks, benefits, and any practical alternatives have been discussed with me in a language I understand. By signing this form, I certify: • That I have read or had this form read and/or explained to me • That I fully understand its contents including the risks and benefits • That I have been given ample opportunity to ask questions and that any questions have been answered to my satisfaction. This consent will remain in effect for all telemedicine services provided to me until I cancel it.`,
];

export default function TelemedicineConsent({
  patientName = "", patientAge = "", practiceName = "", onSubmit,
}) {
  const history   = useHistory();
  const formRef    = useRef(null);
  const sigRef     = useRef(null);
  const [sigEmpty, setSigEmpty]     = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [savedId, setSavedId]       = useState(null);
  const [showToast, Toast]          = useToast();

  const [acknowledged, setAcknowledged] = useState(
    Array(CONSENT_POINTS.length).fill(false)
  );
  const [form, setForm] = useState({
    responsibleParty: patientName,
    relationship:     "Self",
    sigDate:          new Date().toISOString().split("T")[0],
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleAck = (i) =>
    setAcknowledged(a => { const n = [...a]; n[i] = !n[i]; return n; });

  const getSig = () =>
    (!sigRef.current || sigRef.current.isEmpty()) ? null : sigRef.current.toDataURL("image/png");

  const allAcknowledged = acknowledged.every(Boolean);

  const buildDoc = () => ({
    formType:    "telemedicine_consent",
    patient:     { name: patientName, age: patientAge !== "" ? Number(patientAge) : null },
    practiceName,
    consentPoints: CONSENT_POINTS.map((text, i) => ({
      number: i + 1,
      text,
      acknowledged: acknowledged[i],
    })),
    allConsentPointsAcknowledged: allAcknowledged,
    authorization: {
      responsibleParty: form.responsibleParty,
      relationship:     form.relationship,
      sigDate:          form.sigDate || null,
      signatureDataURL: getSig(),
    },
    meta: { submittedAt: new Date().toISOString(), formVersion: "1.0.0" },
  });

  const handleSubmit = async () => {
    if (sigEmpty) { showToast("Please sign before submitting.", "error"); return; }
    setSubmitting(true);
    try {
      const doc = buildDoc();
      const res = typeof onSubmit === "function" ? await onSubmit(doc) : await saveToMongo(doc, API_URL);
      if (res?._id) setSavedId(res._id);
      showToast("Saved! Redirecting…", "success");
      setTimeout(() => history.push(NEXT), 1800);
    } catch (e) { console.error(e); showToast("Save failed.", "error"); }
    finally { setSubmitting(false); }
  };

  const handleDownload = async () => {
    try { await downloadPDF(formRef, `telemedicine-consent-${patientName || "patient"}.pdf`); showToast("Downloaded!"); }
    catch { showToast("PDF failed.", "error"); }
  };

  const practice = practiceName || "your practice";

  return (
    <FormShell
      title="Telemedicine Consent"
      subtitle={`Thank you for choosing ${practice} for your care. Please read and acknowledge each consent point below.`}
      dbLabel="MongoDB · telemedicine_consents"
      patientName={patientName}
      patientAge={patientAge}
      savedId={savedId}
      formRef={formRef}
      onSubmit={handleSubmit}
      onDownload={handleDownload}
      submitting={submitting}
    >
      <div className="wf-section">
        <div className="wf-section-title">
          <span className="num">✓</span>Consent Points — Please read and check each
        </div>

        {CONSENT_POINTS.map((text, i) => (
          <div
            key={i}
            onClick={() => toggleAck(i)}
            style={{
              marginBottom: 14,
              padding: "14px 16px",
              border: `1.5px solid ${acknowledged[i] ? C.teal : C.border}`,
              borderRadius: 10,
              background: acknowledged[i] ? C.tealLt : C.white,
              cursor: "pointer",
              transition: "all .18s",
            }}
          >
            <label style={{ display: "flex", gap: 12, alignItems: "flex-start", cursor: "pointer" }}>
              <input
                type="checkbox"
                style={{ marginTop: 3, accentColor: C.teal, flexShrink: 0, width: 16, height: 16 }}
                checked={acknowledged[i]}
                onChange={() => toggleAck(i)}
                onClick={e => e.stopPropagation()}
              />
              <div>
                <div style={{ fontWeight: 700, fontSize: 12, color: C.teal, marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  Point {i + 1}
                </div>
                <div style={{ fontSize: 13.5, color: C.text, lineHeight: 1.7 }}>{text}</div>
              </div>
            </label>
          </div>
        ))}

        {!allAcknowledged && (
          <div style={{
            marginTop: 8, padding: "10px 16px",
            background: "#FEF3C7", border: "1.5px solid #FCD34D",
            borderRadius: 8, fontSize: 13, color: "#92400E",
          }}>
            ⚠️ Please acknowledge all {CONSENT_POINTS.length} consent points before signing.
          </div>
        )}

        {allAcknowledged && (
          <div style={{
            marginTop: 8, padding: "10px 16px",
            background: C.tealLt, border: `1.5px solid ${C.teal}`,
            borderRadius: 8, fontSize: 13, color: C.teal, fontWeight: 600,
          }}>
            ✅ All consent points acknowledged.
          </div>
        )}
      </div>

      <SigBlock
        sigRef={sigRef}
        sigEmpty={sigEmpty}
        setSigEmpty={setSigEmpty}
        responsibleParty={form.responsibleParty}
        setResponsibleParty={v => set("responsibleParty", v)}
        relationship={form.relationship}
        setRelationship={v => set("relationship", v)}
        sigDate={form.sigDate}
        setSigDate={v => set("sigDate", v)}
      />

      {Toast}
    </FormShell>
  );
}

/*
── Mongoose Schema ────────────────────────────────────────────────────────────
const TelemedicineConsentSchema = new Schema({
  formType:    { type: String, default: "telemedicine_consent" },
  patient:     { name: String, age: Number },
  practiceName: String,
  consentPoints: [{
    number:        Number,
    text:          String,
    acknowledged:  Boolean,
  }],
  allConsentPointsAcknowledged: Boolean,
  authorization: {
    responsibleParty: String,
    relationship:     String,
    sigDate:          Date,
    signatureDataURL: String,
  },
  meta: { submittedAt: Date, formVersion: String },
}, { timestamps: true, collection: "telemedicine_consents" });

── Express route ──────────────────────────────────────────────────────────────
router.post('/', async (req, res) => {
  const doc = new TelemedicineConsentModel(req.body);
  await doc.save();
  res.status(201).json({ _id: doc._id, createdAt: doc.createdAt });
});
router.get('/:id', async (req, res) => {
  const doc = await TelemedicineConsentModel.findById(req.params.id).lean();
  res.json(doc);
});
// Mount at: /api/forms/telemedicine-consent
*/
